import * as Sentry from '@sentry/react';
import { apiClient, ApiRequestError } from '../api';
import amazon from '../config/amazon_us.json';
import type { RetailerConfig, StoreConfig } from '../modules/Config';
import { PurchaseHistory, transformData } from '../modules/DataTransformSchema';
import { useEffect, useRef, useState } from 'react';
import { clientLogger } from '../utils/client-logger';
import type { AmazonPurchase } from '../lib/connector/index.js';
import { useAnalytics } from './useAnalytics';
import {
  type QuestionProTaskStatus,
  useQuestionProTask,
} from './useQuestionProTask';

const POLL_INTERVAL_MS = 3000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export type PanelEligibility = {
  eligible: boolean;
  redirectUrl: string | null;
};

export type DataConnectionResult = {
  getSigninIdAndPollSigninStatus: () => Promise<void>;
  signinId: string | undefined;
  selectedRegion: 'US' | 'CA';
  setSelectedRegion: (region: 'US' | 'CA') => void;
  handleSubmitPreemptiveForm: (credentials: {
    email: string;
    password: string;
  }) => void;
  handleSigninDone: (
    status: 'SUCCESS' | 'ERROR',
    error?: string,
    panelEligibility?: PanelEligibility
  ) => void;
  isFollowUpForm: boolean;
  questionProTaskStatus: QuestionProTaskStatus;
  hasExternalPanel: boolean;
  hasPrefetchedCredentials: boolean;
  shouldRedirectToExternal: boolean;
};
import {
  getPlayerPrefetchResult,
  markPlayerPrefetchSkipped,
} from '../lib/playerPrefetch';
import { useInitialParamsSnapshot } from './useInitialParams';
import { hasExternalPanelParam } from '../config/panels';
import { getSigninError } from '../lib/signinPollUtils';

type UseAmazonDataConnectionProps = {
  store: StoreConfig;
  onSuccessConnect: (data: PurchaseHistory[], isLoadingMore?: boolean) => void;
  onConnectStart?: () => void;
  onConnectionError?: (errorDetails: string, code?: string) => void;
  onPartialSyncError?: (errorDetails: string) => void;
  onProgressStep?: (step: number) => void;
  onAuthComplete?: (panelEligibility?: PanelEligibility) => void;
  onBatchStart?: () => void;
  onRetryConnection?: () => void;
  onGoToInitial?: () => void;
};

const defaultRetailerConfig = amazon as RetailerConfig;

const useAmazonDataConnection = ({
  store,
  onSuccessConnect,
  onConnectStart,
  onConnectionError,
  onPartialSyncError,
  onProgressStep,
  onAuthComplete,
  onBatchStart,
  onRetryConnection,
  onGoToInitial,
}: UseAmazonDataConnectionProps) => {
  const retailerConfig = store.dataTransform ? store : defaultRetailerConfig;

  const SENTRY_TAGS = {
    component: 'DataSource',
    brand_id: store.retailer_id,
    brand_name: store.retailer_name,
  };
  // Keep a ref so poll callbacks always call the latest version (avoids stale closure)
  const onSuccessConnectRef = useRef(onSuccessConnect);
  onSuccessConnectRef.current = onSuccessConnect;
  const onPartialSyncErrorRef = useRef(onPartialSyncError);
  onPartialSyncErrorRef.current = onPartialSyncError;
  const {
    token: tokenFromUrl,
    tid,
    rid,
    psid,
    participantId,
  } = useInitialParamsSnapshot();

  const questionProTaskStatus: QuestionProTaskStatus =
    useQuestionProTask(tokenFromUrl);
  const { track } = useAnalytics();
  const [signinId, setSigninId] = useState<string | undefined>(undefined);
  const [selectedRegion, setSelectedRegion] = useState<'US' | 'CA'>('US');
  const [credentials, setCredentials] = useState<{
    email: string;
    password: string;
  }>();
  const [isFollowUpForm, setIsFollowUpForm] = useState(false);
  const hasPrefetchedCredentials = getPlayerPrefetchResult().hasCredentials;
  const hasStartedRef = useRef(false);
  const hasSubmittedStoredCredentials = useRef(false);

  const fallbackToManualSignin = (reason: string) => {
    clientLogger.warn('Falling back to manual sign-in', { reason });
    markPlayerPrefetchSkipped();
    onRetryConnection?.();
  };

  // Fires on SUCCESS, from runSignin's response or the 2FA iframe's postMessage bridge.
  const handleSigninDone = (
    status: 'SUCCESS' | 'ERROR',
    error?: string,
    panelEligibility?: PanelEligibility
  ) => {
    if (status === 'ERROR') {
      onConnectionError?.(error ?? 'Sign-in failed');
      return;
    }
    onAuthComplete?.(panelEligibility);
    onProgressStep?.(3);
    void loadPurchaseDataStream({ isPostSignin: true });
  };

  const toPurchaseHistory = (rows: AmazonPurchase[]): PurchaseHistory[] =>
    transformData(
      { purchases: rows },
      retailerConfig.dataTransform
    ) as unknown as PurchaseHistory[];

  const loadPurchaseDataStream = async ({ isPostSignin = false } = {}) => {
    // Mark loading before awaiting the trigger call — Amazon's trigger does the
    // real connector tool call synchronously (unlike Walmart's, which returns
    // immediately), so waiting to flip isLoadingMore left a window where
    // orders=[] + isLoadingMore=false rendered "No Orders Found".
    onBatchStart?.();
    onSuccessConnectRef.current([], true);

    try {
      const response = await apiClient.getAmazonPurchaseHistory(
        store.retailer_key,
        selectedRegion
      );

      if (response?.type === 'auth') {
        onSuccessConnectRef.current([], false);
        // Setting signinId triggers the stored-credentials effect below, which
        // submits via runSignin and reaches handleSigninDone on completion.
        setSigninId(response.signin_id);
        return;
      }

      let lastSeenId = 0;
      let allRows: AmazonPurchase[] = [];

      while (true) {
        const {
          syncStatus,
          purchases,
          lastSeenId: nextId,
          syncError,
        } = await apiClient.pollAmazonPurchaseSync(
          store.retailer_key,
          lastSeenId,
          selectedRegion
        );

        if (purchases.length > 0) {
          lastSeenId = nextId;
          allRows = [...allRows, ...purchases];
          onSuccessConnectRef.current(toPurchaseHistory(allRows), true);
        }

        if (syncStatus === 'completed') {
          onSuccessConnectRef.current(toPurchaseHistory(allRows), false);
          return;
        }

        if (syncStatus === 'failed') {
          if (syncError) {
            onPartialSyncErrorRef.current?.(syncError);
          }
          onSuccessConnectRef.current(toPurchaseHistory(allRows), false);
          return;
        }

        await sleep(POLL_INTERVAL_MS);
      }
    } catch (error) {
      clientLogger.info('Purchase sync error:', { error });
      if (!isPostSignin) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        onConnectionError?.(errorMessage);
      }
    }
  };

  const getSigninIdAndPollSigninStatus = async () => {
    try {
      const capacity = await apiClient.checkCapacity();
      if (!capacity.hasCapacity) {
        onConnectionError?.(
          'All sync slots are currently busy. Please retry shortly.',
          'CAPACITY_FULL'
        );
        return;
      }
    } catch (error) {
      clientLogger.info('Capacity check error:', { error });
      Sentry.captureException(error, { tags: SENTRY_TAGS });
      onConnectionError?.(
        'All sync slots are currently busy. Please retry shortly.',
        'CAPACITY_FULL'
      );
      return;
    }

    try {
      const response = await apiClient.getSigninId(
        store.retailer_key,
        selectedRegion
      );
      if (response.signin_id) {
        setSigninId(response.signin_id);
      }
    } catch (error) {
      clientLogger.info('Get signin id error:', { error });
      Sentry.captureException(error, { tags: SENTRY_TAGS });
      if (error instanceof ApiRequestError && error.status === 503) {
        onConnectionError?.(error.message, 'CAPACITY_FULL');
        return;
      }
      onConnectionError?.('Failed to sign in');
    }
  };

  const handleSubmitPreemptiveForm = ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    setCredentials({ email, password });
    onConnectStart?.();
  };

  const runSignin = async (
    submit: () => Promise<
      | { type: 'SUCCESS'; panelEligibility?: PanelEligibility }
      | { type: 'NEED_SIGNIN' }
      | { type: 'ERROR'; error: string }
    >,
    errorPrefix: string,
    onFailure: (message: string, raw?: string) => void
  ) => {
    try {
      const response = await submit();
      if (response.type === 'SUCCESS') {
        handleSigninDone('SUCCESS', undefined, response.panelEligibility);
        return;
      }
      if (response.type === 'NEED_SIGNIN') {
        track('Follow Up Form Shown');
        setIsFollowUpForm(true);
        onGoToInitial?.();
        return;
      }
      if (response.type === 'ERROR') {
        Sentry.captureException(
          new Error(`${errorPrefix}: ${response.error}`),
          { tags: SENTRY_TAGS }
        );
        onFailure(`${errorPrefix}: ${response.error}`, response.error);
      }
    } catch (err) {
      Sentry.captureException(err, { tags: SENTRY_TAGS });
      const { message, code } = getSigninError(err);
      onFailure(message, code);
    }
  };

  useEffect(() => {
    if (!credentials?.email || !signinId) return;
    runSignin(
      () => apiClient.signin(signinId, credentials),
      'Error submitting login form',
      (msg, raw) => onConnectionError?.(msg, raw)
    );
    // oxlint-disable-next-line react/exhaustive-deps
  }, [credentials, signinId]);

  useEffect(() => {
    if (!hasPrefetchedCredentials || hasStartedRef.current) return;
    hasStartedRef.current = true;
    onConnectStart?.();
    getSigninIdAndPollSigninStatus();
    // oxlint-disable-next-line react/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !hasPrefetchedCredentials ||
      !signinId ||
      hasSubmittedStoredCredentials.current
    )
      return;
    hasSubmittedStoredCredentials.current = true;
    runSignin(
      () => apiClient.signin(signinId, { useStoredCredentials: true }),
      'Stored credentials rejected',
      fallbackToManualSignin
    );
    // oxlint-disable-next-line react/exhaustive-deps
  }, [hasPrefetchedCredentials, signinId]);

  return {
    getSigninIdAndPollSigninStatus,
    signinId,
    selectedRegion,
    handleSubmitPreemptiveForm,
    handleSigninDone,
    setSelectedRegion,
    isFollowUpForm,
    questionProTaskStatus,
    hasExternalPanel: hasExternalPanelParam({
      rid,
      psid,
      tid,
      participant_id: participantId,
    }),
    hasPrefetchedCredentials,
    shouldRedirectToExternal:
      questionProTaskStatus === 'valid' ||
      hasExternalPanelParam({ rid, psid, tid, participant_id: participantId }),
  };
};

export default useAmazonDataConnection;
